"use client";

import React from "react";

import classNames from "classnames";

import { Card } from "@/components/flowbite/card";
import { StudentCountsInShapeColorByEnum } from "@/providers/store/api/shapes/enums/student-counts-in-shape.enum";

import { ClusterSelectionSummarySkeleton } from "./cluster-selection/cluster-selection-summary-skeleton";
import { SectionTitle } from "./section-title";
import { SkeletonBox } from "./skeleton-box";
import StudentBy from "./student-by";
import { STUDENT_BY_TITLE_DICTIONARY } from "./student-by.constants";
import { Summary } from "./summary";

import type { IBoundaryPayload } from "../../map-panel-context";

interface BoundaryPlottingSkeletonProps {
  schoolCountsOnly?: boolean;
  colorBy?: StudentCountsInShapeColorByEnum;
}

function getPlottingSkeletonMetricCount(
  colorBy: StudentCountsInShapeColorByEnum,
): 3 | 4 {
  return colorBy === StudentCountsInShapeColorByEnum.GRADE ? 4 : 3;
}

export const BoundaryPlottingSkeleton: React.FC<BoundaryPlottingSkeletonProps> = ({
  schoolCountsOnly = true,
  colorBy = StudentCountsInShapeColorByEnum.NONE,
}) => {
  const showStudentBySkeleton =
    !schoolCountsOnly && colorBy !== StudentCountsInShapeColorByEnum.NONE;

  return (
    <div className="space-y-6">
      <ClusterSelectionSummarySkeleton
        metricCount={
          schoolCountsOnly ? 1 : getPlottingSkeletonMetricCount(colorBy)
        }
        studentsFullWidth={
          !schoolCountsOnly && colorBy !== StudentCountsInShapeColorByEnum.GRADE
        }
      />
      {showStudentBySkeleton ? (
        <div>
          <SectionTitle title={STUDENT_BY_TITLE_DICTIONARY[colorBy]} />
          <div className="mt-2 space-y-1">
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className="flex gap-2"
              >
                <SkeletonBox className="h-4 w-20 shrink-0" />
                <SkeletonBox className="h-4 flex-1" />
                <SkeletonBox className="h-4 w-10 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface BoundaryPanelSkeletonProps {
  payload?: IBoundaryPayload | null;
}

export const BoundaryPanelSkeleton: React.FC<BoundaryPanelSkeletonProps> = () => {
  return (
    <div className="space-y-6 px-3 py-4">
      <div>
        <SectionTitle title="Selected Boundary" />
        <Card className="mt-2 flex w-full min-w-0 items-center gap-1 py-1 pl-3 pr-2">
          <SkeletonBox className="h-10 w-10 shrink-0 rounded" />
          <SkeletonBox className="h-4 flex-1" />
        </Card>
      </div>

      <div>
        <SectionTitle title="Connected School" />
        <div className="mt-2 space-y-3">
          <div className="flex min-w-0 items-center gap-3">
            <SkeletonBox className="h-4 w-[100px] shrink-0" />
            <SkeletonBox className="h-10 flex-1" />
          </div>
        </div>
      </div>

      <BoundaryPlottingSkeleton />
    </div>
  );
};

interface BoundaryPanelPlottingSectionProps {
  payload: IBoundaryPayload;
}

export const BoundaryPanelPlottingSection: React.FC<BoundaryPanelPlottingSectionProps> = ({
  payload,
}) => {
  if (payload.isPlottingLoading) {
    return (
      <BoundaryPlottingSkeleton
        colorBy={payload.plotting?.colorBy}
        schoolCountsOnly={payload.plotting?.useSchoolCountsOnly ?? true}
      />
    );
  }

  if (!payload.plotting) {
    return null;
  }

  return (
    <div className={classNames("space-y-6")}>
      <Summary
        dataCount={payload.plotting.dataCount}
        schoolCountsOnly={payload.plotting.useSchoolCountsOnly}
        schoolsCount={payload.plotting.schoolsCount}
        schoolsPlotted={payload.plotting.schoolsPlotted}
        studentsCount={payload.plotting.studentsCount}
        type={payload.plotting.colorBy}
      />
      {!payload.plotting.useSchoolCountsOnly ? (
        <StudentBy
          data={payload.plotting.dataBy}
          type={payload.plotting.colorBy}
        />
      ) : null}
    </div>
  );
};
